// ═══════════════════════════════════════════════════════════════════════════
// ErrorBoundary — wrapper para páginas/componentes críticos (V3.7.3)
// ═══════════════════════════════════════════════════════════════════════════
//
// React Error Boundaries atrapan errores en el render tree antes de que
// rompan toda la app. Sin esto, una excepción en un componente cualquiera
// genera una pantalla blanca para el usuario.
//
// USO
// ───
//   import ErrorBoundary from '@core/components/ErrorBoundary'
//
//   <ErrorBoundary scope="OpinionesDashboard">
//     <OpinionesDashboard />
//   </ErrorBoundary>
//
//   // O con fallback custom:
//   <ErrorBoundary scope="X" fallback={({ error, reset }) => <MiFallback ... />}>
//     <ChildComponent />
//   </ErrorBoundary>
//
// COMPORTAMIENTO
// ──────────────
// - El error se loggea via `log.error` (que persiste a Firestore en prod).
// - Muestra UI de fallback minimalista con botón "Reintentar".
// - El reset re-renderea el subtree.
// - Con `resetKey`, el fallback se va solo cuando la key cambia. Las rutas usan
//   `ErrorBoundaryDeRuta`, que la ata a la URL: React Router no desmonta la
//   página al cambiar el parámetro (/casos/A → /casos/B) ni la query (?tab=),
//   así que sin esto el error de A quedaba bajo la URL de B hasta «Reintentar».
//   Sólo resetea en estado de error: una página sana no se remonta.
//
// DETECCIÓN DE CHUNKS VIEJOS (19-may-2026, fix de 79% de los crashes)
// ─────────────────────────────────────────────────────────────────────
// Vite con code-splitting genera chunks JS con hash en el nombre. Cuando
// hay deploy nuevo, los hashes cambian y los chunks viejos dejan de existir
// en el CDN. Si el usuario tiene la pestaña abierta o navega entre vistas
// SPA, el browser intenta cargar un chunk que ya no está y tira:
//     "Failed to fetch dynamically imported module: .../assets/X-hash.js"
//
// Esto NO es un crash de código, es esperado tras deploys. Detectamos el
// patrón y forzamos `window.location.reload()` con un toast amigable.
// Anti-loop: si ya recargamos hace <60s, no volvemos a recargar (sino quedaría
// en bucle si por alguna razón el reload no resuelve).
// ═══════════════════════════════════════════════════════════════════════════

import { Component } from 'react'
import { useLocation } from 'react-router-dom'
import { createLogger } from '@core/lib/logger'

const CHUNK_RELOAD_KEY = 'mb_chunk_reload_at'
const CHUNK_RELOAD_COOLDOWN_MS = 60_000 // 60s — si ya recargamos hace menos, no insistir

/**
 * Detecta si el error es un chunk JS viejo que ya no existe en el server.
 * Patrones cubiertos:
 *   - "Failed to fetch dynamically imported module" (Vite/Chrome principal)
 *   - "Importing a module script failed" (Firefox)
 *   - "Loading chunk N failed" (Webpack-like, por si acaso)
 *   - "ChunkLoadError" (Webpack name)
 */
function esErrorChunkViejo(error) {
  if (!error) return false
  const msg = String(error.message || '')
  const name = String(error.name || '')
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    name === 'ChunkLoadError'
  )
}

/**
 * ¿Acabamos de recargar por chunk viejo? Anti-loop infinito.
 * Si reloads consecutivos por chunks no resuelven, dejamos al user en
 * el fallback normal para que vea el error y reporte.
 */
function reloadReciente() {
  try {
    const ts = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
    if (!ts) return false
    return Date.now() - ts < CHUNK_RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

function marcarReload() {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  } catch { /* graceful */ }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, recargando: false }
    this._log = createLogger(`ErrorBoundary:${props.scope || 'unknown'}`)
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    const esChunk = esErrorChunkViejo(error)

    if (esChunk && !reloadReciente()) {
      // Caso A: chunk viejo + primer intento → auto-reload silencioso.
      // Loggeamos como info (no error) — es comportamiento esperado tras deploy.
      this._log.info('boundary.chunk_outdated_reloading', {
        message: error.message,
      })
      marcarReload()
      this.setState({ recargando: true })
      // Pequeño delay para que el toast se vea y el log se persista.
      setTimeout(() => { window.location.reload() }, 1200)
      return
    }

    if (esChunk && reloadReciente()) {
      // Caso B: chunk viejo + ya recargamos hace <60s → algo está roto, no
      // insistir con reload. Caer al fallback normal pero loggear distinto
      // para diferenciar en clientLogs.
      this._log.error('boundary.chunk_reload_loop', error, {
        componentStack: errorInfo?.componentStack?.slice(0, 2000) || null,
        hint: 'Reload reciente no resolvió el chunk missing — revisar deploy o CDN',
      })
      return
    }

    // Caso C: error normal de código → comportamiento histórico (loggear + fallback).
    // Sin `url` en el ctx, acá ni en los otros dos: la entrada de `clientLogs` ya
    // la guarda limpia (`urlSegura`), y el ctx no tapa un token en el path.
    this._log.error('boundary.caught', error, {
      componentStack: errorInfo?.componentStack?.slice(0, 2000) || null,
    })
  }

  componentDidUpdate(prevProps) {
    // Durante el auto-reload por chunk viejo no se resetea: la página se va a
    // recargar igual y re-renderear sólo volvería a pedir el chunk que no está.
    if (this.state.hasError && !this.state.recargando && prevProps.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, recargando: false })
  }

  render() {
    if (this.state.recargando) {
      return <ChunkReloadFallback />
    }

    if (this.state.hasError) {
      const { fallback, scope } = this.props
      const error = this.state.error

      if (typeof fallback === 'function') {
        return fallback({ error, reset: this.reset })
      }

      return <DefaultFallback error={error} scope={scope} reset={this.reset} />
    }
    return this.props.children
  }
}

/**
 * Fallback que se muestra brevemente (~1.2s) antes de que el reload tome efecto.
 * UX clave: el usuario ve un mensaje claro de "nueva versión, recargando" en
 * vez de un crash, y no toca botones.
 */
function ChunkReloadFallback() {
  return (
    <div className="min-h-[300px] flex items-center justify-center p-8">
      <div className="max-w-md text-center space-y-4 glass-card p-6">
        <div className="w-14 h-14 mx-auto rounded-full bg-brand-accent/[0.12] border border-brand-accent/30 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-7 h-7 text-brand-accent animate-spin">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
        <div>
          <h2 className="text-[16px] font-bold text-white">Hay una versión nueva</h2>
          <p className="text-[13px] text-zinc-400 mt-1.5 leading-relaxed">
            Estamos recargando la página para traerte la versión actualizada…
          </p>
        </div>
      </div>
    </div>
  )
}

function DefaultFallback({ error, scope, reset }) {
  return (
    <div className="min-h-[300px] flex items-center justify-center p-8">
      <div className="max-w-lg text-center space-y-4 glass-card p-6">
        <div className="w-14 h-14 mx-auto rounded-full bg-red-500/[0.12] border border-red-500/30 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-7 h-7 text-red-400">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div>
          <h2 className="text-[16px] font-bold text-white">Algo salió mal</h2>
          <p className="text-[13px] text-zinc-400 mt-1.5 leading-relaxed">
            Esta sección{scope ? ` (${scope})` : ''} encontró un error inesperado y no pudo renderizarse. Ya quedó registrado en los logs.
          </p>
        </div>
        {error?.message && (
          <pre className="text-[10.5px] text-zinc-500 bg-black/30 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all text-left max-h-32">
            {error.message}
          </pre>
        )}
        <div className="flex gap-2 justify-center">
          <button
            onClick={reset}
            className="px-4 py-2 rounded-lg bg-brand-accent text-black text-[13px] font-semibold hover:bg-brand-accent/90 transition-colors"
          >
            Reintentar
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-[13px] text-zinc-300 hover:bg-white/[0.1] transition-colors"
          >
            Recargar página
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * ErrorBoundary de una ruta: el fallback se resetea al cambiar la URL
 * (pathname + query). Report oKERyrsKwZx9mqMxvIJO, punto 5.
 */
export function ErrorBoundaryDeRuta(props) {
  const { pathname, search } = useLocation()
  return <ErrorBoundary {...props} resetKey={pathname + search} />
}

export default ErrorBoundary
